import { createServerFn } from "@tanstack/react-start";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  GoalIdSchema,
  GoalInProgramSchema,
  LinkGoalToProgramSchema,
  PlaceProgramGoalSchema,
} from "~/lib/schemas/goal-link";

/** The goal's programs, and the client's other active programs it can join. */
export const listGoalProgramsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GoalIdSchema)
  .handler(async ({ data, context }) => {
    const { listGoalPrograms } = await import("~/lib/server/goal-link");
    return listGoalPrograms(context.user.id, data.goalId);
  });

/** The coaches of the programs this goal is linked to; its saves wrap keys for them too. */
export const getLinkedCoachTargetsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GoalIdSchema)
  .handler(async ({ data, context }) => {
    const { getLinkedCoachTargets } = await import("~/lib/server/goal-link");
    return getLinkedCoachTargets(context.user.id, data.goalId);
  });

/** The coach to wrap keys for and the client's own keys on the goal and its steps. */
export const prepareGoalLinkFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GoalInProgramSchema)
  .handler(async ({ data, context }) => {
    const { prepareGoalLink } = await import("~/lib/server/goal-link");
    return prepareGoalLink(context.user.id, data);
  });

/** The client suggests her Life Design goal to a program, with the coach's keys. */
export const linkGoalToProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(LinkGoalToProgramSchema)
  .handler(async ({ data, context }) => {
    const { linkGoalToProgram } = await import("~/lib/server/goal-link");
    await linkGoalToProgram(context.user.id, data);
  });

/** The client removes her Life Design goal from a program. */
export const unlinkGoalFromProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(GoalInProgramSchema)
  .handler(async ({ data, context }) => {
    const { removeGoalFromProgram } = await import("~/lib/server/goal-link");
    await removeGoalFromProgram({ userId: context.user.id }, data);
  });

/** The coach removes a client's Life Design goal from their program. */
export const removeGoalFromProgramFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(GoalInProgramSchema)
  .handler(async ({ data, context }) => {
    const { removeGoalFromProgram } = await import("~/lib/server/goal-link");
    await removeGoalFromProgram({ coachId: context.coach.id }, data);
  });

/** The program goals planned under the client's Life Design goal. */
export const listProgramGoalsUnderFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(GoalIdSchema)
  .handler(async ({ data, context }) => {
    const { listProgramGoalsUnder } = await import("~/lib/server/goal-link");
    return listProgramGoalsUnder(context.user.id, data.goalId);
  });

/** The client places one of her program goals under one of her Life Design goals. */
export const placeProgramGoalUnderFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(PlaceProgramGoalSchema)
  .handler(async ({ data, context }) => {
    const { placeProgramGoalUnder } = await import("~/lib/server/goal-link");
    await placeProgramGoalUnder(context.user.id, data);
  });
